import { env } from './env';

/**
 * Site mode gate (D32): Firebase Remote Config decides whether the app is open (`live`),
 * behind the launch countdown, or in maintenance. UI-only: rules still protect the data.
 */
export type SiteMode = 'live' | 'countdown' | 'maintenance';

export interface SiteConfig {
  mode: SiteMode;
  launchAt: Date;
  maintenanceMessage: string;
  maintenanceUntil: Date | null;
}

/** Remote Config parameter keys (remoteconfig.template.json). */
export interface RawSiteConfig {
  site_mode?: string;
  launch_at?: string;
  maintenance_message?: string;
  maintenance_until?: string;
}

/** Sun 1 Nov 2026, 12:00 am Seattle time (PDT): 7 days after Sun 25 Oct 2026, 12:00 am. */
export const DEFAULT_LAUNCH_AT = '2026-11-01T07:00:00Z';
export const COUNTDOWN_DAYS = 7;

const MODES: readonly SiteMode[] = ['live', 'countdown', 'maintenance'];

export function isSiteMode(value: unknown): value is SiteMode {
  return typeof value === 'string' && (MODES as readonly string[]).includes(value);
}

function parseDate(value: string | undefined): Date | null {
  if (!value?.trim()) return null;
  const d = new Date(value.trim());
  return Number.isNaN(d.getTime()) ? null : d;
}

/** The build's fallback mode; `live` unless VITE_SITE_MODE_DEFAULT names a valid mode. */
export function defaultSiteMode(): SiteMode {
  return isSiteMode(env.siteModeDefault) ? env.siteModeDefault : 'live';
}

/** Defensive parse: unknown modes and bad dates fall back instead of breaking the gate. */
export function parseSiteConfig(raw: RawSiteConfig, fallbackMode = defaultSiteMode()): SiteConfig {
  const mode = raw.site_mode?.trim().toLowerCase();
  return {
    mode: isSiteMode(mode) ? mode : fallbackMode,
    launchAt: parseDate(raw.launch_at) ?? new Date(DEFAULT_LAUNCH_AT),
    maintenanceMessage: raw.maintenance_message?.trim() ?? '',
    maintenanceUntil: parseDate(raw.maintenance_until),
  };
}

/** The config known without a network call: the local override, or null when Remote Config decides. */
export function localSiteConfig(): SiteConfig | null {
  if (env.siteModeOverride) return parseSiteConfig({ site_mode: env.siteModeOverride });
  // The demo project has no Remote Config; emulator runs are always open.
  if (env.useEmulators) return parseSiteConfig({ site_mode: 'live' });
  return null;
}

/**
 * Loads the config from Remote Config and reports every change (real-time updates, plus a
 * re-fetch when the tab becomes visible). Order: cached value from an earlier visit (shown at
 * once), then a fresh fetch, then the build default. Returns an unsubscribe function.
 */
export function watchSiteConfig(onChange: (config: SiteConfig) => void): () => void {
  let stopped = false;
  let cleanup = () => {};

  void (async () => {
    try {
      const rc = await import('firebase/remote-config');
      const { app } = await import('./firebase');
      if (stopped) return;
      if (!(await rc.isSupported())) throw new Error('Remote Config is not supported here');

      const config = rc.getRemoteConfig(app);
      config.settings.minimumFetchIntervalMillis = 60_000;
      config.settings.fetchTimeoutMillis = 5_000;
      config.defaultConfig = {
        site_mode: defaultSiteMode(),
        launch_at: DEFAULT_LAUNCH_AT,
        maintenance_message: '',
        maintenance_until: '',
      };
      const emit = () => {
        if (stopped) return;
        onChange(
          parseSiteConfig({
            site_mode: rc.getString(config, 'site_mode'),
            launch_at: rc.getString(config, 'launch_at'),
            maintenance_message: rc.getString(config, 'maintenance_message'),
            maintenance_until: rc.getString(config, 'maintenance_until'),
          }),
        );
      };

      await rc.ensureInitialized(config);
      if (rc.getValue(config, 'site_mode').getSource() === 'remote') emit();
      await rc.fetchAndActivate(config).catch(() => false);
      emit();
      if (stopped) return;

      const unsubscribe = rc.onConfigUpdate(config, {
        next: () => void rc.activate(config).then(emit, () => {}),
        // The stream can drop (offline, blocked); the visibility re-fetch still catches flips.
        error: () => {},
        complete: () => {},
      });
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          void rc.fetchAndActivate(config).then(emit, () => {});
        }
      };
      document.addEventListener('visibilitychange', onVisible);
      cleanup = () => {
        unsubscribe();
        document.removeEventListener('visibilitychange', onVisible);
      };
    } catch {
      if (!stopped) onChange(parseSiteConfig({}));
    }
  })();

  return () => {
    stopped = true;
    cleanup();
  };
}
